import { Injectable } from '@angular/core';
import {RequesterService} from "../../../../shared/services/requester/requester.service";
import {BehaviorSubject} from "rxjs";
import {DbResponse} from "../../../../shared/interceptors/db-response";
import {Store} from "@ngrx/store";
import * as fromStore from '../../../../store';
import {TypeUtils} from "../../../../shared/utils/type-utils";
import {HostingService} from "../../hosting/hosting.service";
import {filter, first, map, switchMap} from "rxjs/operators";
import {BoosterTroubleInstructionsComponent} from "../dialogs/booster-trouble-instructions/booster-trouble-instructions.component";
import {Router} from "@angular/router";
import {DbModalService} from "../../../../modules/modal/modal";
import {UserSubscriptionType} from "../../../../user/user-subscription";
import {GoogleAnalyticsService} from "../../../../shared/services/google-analytics/google-analytics.service";
import {DialogService} from "../../../../../ng2-bootstrap-modal/dialog.service";
import {DbStoreState} from "../../../../store";
import {combineLatest} from "rxjs";
import {CreateWebsiteService} from "../../../../services/local/create-website.service";
import {Config} from "../../../../config/config";
import mockData from "../../../../../assets/json/mock-data.json";

export type BoosterMode = 'standard' | 'balanced' | 'strong' | 'extreme' | 'no_optimize' | 'pro' | string | null;

@Injectable({
  providedIn: 'root'
})
export class BoosterService {

  private _ownerWorkspaceId: number;
  private _currentWorkspaceId: number;
  private _isPaid: boolean;
  private _checkAvailability: boolean;

  constructor(
    private _requester: RequesterService,
    private _hostingService: HostingService,
    private _modalService: DbModalService,
    private _dialogService: DialogService,
    private _router: Router,
    private _store: Store<DbStoreState>,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _createWebsiteService: CreateWebsiteService,
  ) {
    this._getWorkspaceEntities();
  }

  private _getWorkspaceEntities(): void {
    combineLatest(
      this._store.select(fromStore.getUserSubscription),
      this._store.select(fromStore.getWorkspaceIdEntities)
    )
      .subscribe(([userSubscription, workspaceIdEntities]) => {
        this._ownerWorkspaceId = workspaceIdEntities.ownerWorkspaceId;
        this._currentWorkspaceId = workspaceIdEntities.currentWorkspaceId || this._ownerWorkspaceId;
        this._isPaid = userSubscription && userSubscription.type !== UserSubscriptionType.FREE;
      });
  }
  /** pageId: 'all' is for entire website. pageId: front_page is for home page **/
  public applyBoosterMode(websiteId: number, pageId: number | 'front_page' | 'all', mode: BoosterMode, isCustom: number = 0, pageUrl: any = null): BehaviorSubject<DbResponse> {
    const params = {
      pageId,
    };

    params['mode'] = !TypeUtils.isUndefined(mode) && mode ? mode : 'global';
    params['isCustom'] = !TypeUtils.isUndefined(isCustom) ? isCustom : 0;
    if (pageUrl) {
      params['pageUrl'] = pageUrl;
      params['optimizeImages'] = true;
    }
    return <BehaviorSubject<DbResponse>>this._requester.performance
      .post(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/apply-mode`, null, params, true);
  }

  public clearCache(websiteId): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>this._requester.performance
      .post(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/clear-cache`, null, null);
  }

  public pageCache(websiteId, status): BehaviorSubject<DbResponse> {
    const data = {
      status: status,
    };
    return <BehaviorSubject<DbResponse>>this._requester.performance
      .post(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/page-cache`, null, data)
      .pipe(filter(response => !!response),
        map((response: any) => {
          if (response && response.success) {
            return response;
          }
          else {
            this._checkWebsiteAvailability(websiteId);
          }
        }));
  }

  public getClearCacheStatus(websiteId: number): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>this._requester.performance
      .get(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/get-page-cache-status`, null, true)
      .pipe(filter(response => !!response),
        map((response: DbResponse) => {
          if (response && response.success) {
            return response;
          }
          else {
            this._checkWebsiteAvailability(websiteId);
          }
          return null;
        }));
  }

  public getPageId(websiteId: number, pageUrl: string): BehaviorSubject<DbResponse> {
    const params = {
      pageUrl
    };
    return <BehaviorSubject<DbResponse>>this._requester.performance
      .post(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/get-page-id`, null, params, true);
  }

  public setTestMode(websiteId: number, mode: 'on'|'off'): BehaviorSubject<DbResponse> {
    const data = {
      key: 'two_test_mode',
      val: mode,
    };

    return <BehaviorSubject<DbResponse>>this._requester.performance
      .post(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/set-option`, null, data, true);
  }

  public getOptimizationData(websiteId: number): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['optimization_data'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>this._requester.performance
      .get(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/optimization_data`, null, true)
      .pipe(filter(response => !!response),
        map((response: DbResponse) => {
          /*TODO change data.data*/
          if (response && response.success && response.data && response.data.get_modes) {
            return response;
          } else {
            this._checkWebsiteAvailability(websiteId);
          }
          return null;
        }));
  }

  public getOptimizeStatus(websiteId: number, pageUrl: string): BehaviorSubject<DbResponse> {
    const data = {
      pageUrl: pageUrl,
    };
    return <BehaviorSubject<DbResponse>>this._requester.performance
      .post(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/get-optimize-status`, null, data);
  }

  public getOptimizerStats(websiteId: number): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId || !this._currentWorkspaceId) {
      const response: any = mockData['stat'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>this._requester.optimizer
      .get(`compress/workspaces/${this._currentWorkspaceId}/domains/${websiteId}/stat`, null, true)
      .pipe(filter(response => !!response),
        map((response: DbResponse) => {
          if (response && response.success) {
            return response;
          }
          else {
            this._checkWebsiteAvailability(websiteId);
          }
          return null;
        }));
  }

  public optimizeImages(websiteId: number): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>this._requester.optimizer
      .post(`compress/workspaces/${this._currentWorkspaceId}/domains/${websiteId}/optimize`, null, null);
  }

  public optimizerSettingsStore(websiteId, autoOptimize): BehaviorSubject<DbResponse> {
    const data = {
      enable_auto_optimization: +autoOptimize,
    };
    return <BehaviorSubject<DbResponse>>this._requester.optimizer
      .post(`settings/workspaces/${this._currentWorkspaceId}/domains/${websiteId}/store`, null, data);
  }

  public setWebpStatus(websiteId: number, webpStatus: boolean, pictureWebpStatus: boolean): BehaviorSubject<DbResponse> {
    const params = {
      webp_delivery: webpStatus,
      picture_webp_delivery: pictureWebpStatus,
    };
    return <BehaviorSubject<DbResponse>>this._requester.performance
      .post(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/set-webp-status`, null, params);
  }

  public optimizerSettingsGet(websiteId: number): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['get'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>this._requester.optimizer
      .get(`settings/workspaces/${this._currentWorkspaceId}/domains/${websiteId}/get`, null, true)
      .pipe(filter(response => !!response),
        map((response: DbResponse) => {
          if (response && response.success) {
            return response;
          }
          else {
            this._checkWebsiteAvailability(websiteId);
          }
          return  null;
        }));
  }

  public removeOptimizedPage(websiteId: number, pageId: number | string): BehaviorSubject<DbResponse> {
    const params = {
      pageId
    };
    return <BehaviorSubject<DbResponse>>this._requester.performance
      .post(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/delete-so-page`, null, params);
  }

  public getBenchmarkData(websiteId, aiBuilderDemoWorkspaceId?: number): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['benchmark-data'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>this._requester.performance
      .get(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/benchmark-data`, null, null)
      .pipe(filter(response => !!response),
        map((response: DbResponse) => {
          if (response && response.success) {
            return response;
          }
          else {
            this._checkWebsiteAvailability(websiteId);
          }
          return null;
        }));
  }

  public rerunBenchmark(websiteId): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>this._requester.performance
      .post(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/rerun-benchmark`, null, null);
  }

  private _checkWebsiteAvailability(websiteId: number): void {
    this._hostingService.checkWebsiteAvailability(websiteId)
      .pipe(
        first(response => !!response),
        map(response => response.success)
      ).subscribe(isAvailable => {
      if (!isAvailable && !this._checkAvailability && !this._modalService.hasOpenModals()) {
        this._checkAvailability = true;
        this._getBoosterAndManagerPlugins(websiteId);
      }
    });
  }

  private _getBoosterAndManagerPlugins(websiteId): void {
    this._store.select(fromStore.getWebsitePluginsLoaded(websiteId))
      .pipe(
        first(loaded => loaded, null),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getWebsiteOptimizerPlugin(websiteId)),
          this._store.select(fromStore.getWebsiteImageOptimizerPlugin(websiteId)),
          this._store.select(fromStore.getWebsiteManagerPlugin(websiteId)),
          this._store.select(fromStore.getWebsite(websiteId)),
        )),
        first()
      )
      .subscribe(([boosterPlugin, ioPlugin, managerPlugin, website]) => {
        if (website && !!website.domain_hash) {
          this._router.navigate(['/websites']);
        }
        else if ((!boosterPlugin || (boosterPlugin && !boosterPlugin.active))
          && managerPlugin && managerPlugin.active) {
          this._createWebsiteService.openMigrateWebsiteModal(websiteId, website ? website.site_url : null);
        }
        else if (website && !website.cloudflareEnabled) {
          const modal = this._modalService.open(BoosterTroubleInstructionsComponent, {
            width: 700
          });
          modal.componentInstance.isPaid = this._isPaid;
          modal.componentInstance.website = website;
          modal.componentInstance.isIO = ioPlugin && !!ioPlugin.active;
        }
        website && !website.cloudflareEnabled && this._router.navigate(['/websites']);
      });
  }

  public getAdvancedOptions(websiteId): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>this._requester.performance
      .get(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/get-options`, null, null);
  }

  public getInitialMode(websiteId): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>this._requester.performance
      .get(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/get-initial-mode`, null, null);
  }

  public setAdvancedOptions(websiteId, data): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>this._requester.performance
      .post(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/set-option`, null, data);
  }

  /**use this route when user clicks 'optimize' from the booster plugin (WP) and has connected website with IO**/
  public runBoosterFlow(websiteId: number): BehaviorSubject<DbResponse> {
    return this._requester.manager.post(`domains/${websiteId}/products/run-booster-flow`, null);
  }

  public getAdvancedSettings(websiteId: number): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['advanced_settings'];
      return new BehaviorSubject<DbResponse>(response);
    }
    const filter = {
      filter: "two_delay_all_js_execution,two_exclude_delay_js,two_delay_js_exclusions,two_aggregate_js,two_exclude_js,two_minify_js,two_critical_status,two_aggregate_css,two_exclude_css,two_include_inline_css,two_minify_css,two_font_actions,two_add_overflow_to_page,two_lazyload,two_bg_lazyload,two_iframe_lazyload,two_video_lazyload"
    };
    return this._requester.performance.get(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/two-settings`, filter, true);
  }

  public setAdvancedSettings(websiteId: number, data: any): BehaviorSubject<DbResponse> {
    const params = {
      "settings":  JSON.stringify(data)
    };
    return <BehaviorSubject<DbResponse>>this._requester.performance
      .post(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/two-settings`, null, params);
  }
}
